import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  CallSplit as LogicIcon,
  SkipNext as SkipIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  AccountTree as BranchIcon
} from '@mui/icons-material';
import Tutorial from './Tutorial';

const SurveyLogicTutorial = () => {
  const tutorialSteps = [
    {
      title: "Understanding Survey Logic",
      description: "Learn the basics of conditional questions and branching",
      duration: 3,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Survey logic allows you to create dynamic surveys that adapt based on respondent answers. 
            This makes surveys more relevant and reduces survey fatigue by only showing applicable questions.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Types of Survey Logic:
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 2, border: '2px solid', borderColor: 'primary.light' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SkipIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Skip Logic
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Skip irrelevant questions based on previous answers. For example, if someone answers "No" 
                to "Do you own a car?", skip all car-related questions.
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ShowIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Show/Hide Logic
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Show additional questions only when certain conditions are met. Perfect for follow-up 
                questions or gathering more details.
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BranchIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Branching Logic
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Create different paths through your survey based on respondent characteristics or answers. 
                Each path can have completely different questions.
              </Typography>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Best Practice:</strong> Start simple with basic skip logic, then add more complex 
              branching as needed. Too much logic can make surveys confusing to manage.
            </Typography>
          </Alert>
        </Box>
      ),
      screenshot: {
        src: "/images/screenshots/tutorial-logic-overview.png",
        alt: "Survey Logic Overview",
        caption: "Visual representation of how survey logic creates different paths"
      },
      actions: [
        "Open an existing survey or create a new one",
        "Navigate to the survey builder",
        "Identify questions that could benefit from logic",
        "Plan your logic flow on paper first"
      ],
      tips: [
        "Map out your logic flow before implementing it",
        "Test all possible paths through your survey",
        "Keep logic simple to avoid confusion",
        "Document complex logic for future reference"
      ]
    },
    {
      title: "Setting Up Skip Logic",
      description: "Create conditional questions that skip irrelevant sections",
      duration: 4,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Skip logic is the most common type of survey logic. It allows you to skip questions or 
            entire sections based on how respondents answer previous questions.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Common Skip Logic Scenarios:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Product Usage" 
                secondary="Skip product-specific questions for non-users"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Demographics" 
                secondary="Skip age-specific questions based on age ranges"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Satisfaction" 
                secondary="Skip improvement questions for highly satisfied customers"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Experience Level" 
                secondary="Skip basic questions for experienced users"
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Always test your skip logic thoroughly. Make sure respondents 
              can't get stuck in loops or miss important questions.
            </Typography>
          </Alert>
        </Box>
      ),
      screenshot: {
        src: "/images/screenshots/tutorial-skip-logic-setup.png",
        alt: "Skip Logic Setup Interface",
        caption: "Setting up skip logic conditions in the survey builder"
      },
      actions: [
        "Select the question you want to add logic to",
        "Click on the 'Logic' tab in the question properties panel",
        "Choose 'Skip Logic' from the logic type dropdown",
        "Set the condition (e.g., 'If answer is No')",
        "Choose what to skip (next question, section, or end survey)",
        "Save and test the logic"
      ],
      tips: [
        "Use clear, unambiguous answer options for logic triggers",
        "Consider all possible answer combinations",
        "Test with different response patterns",
        "Provide clear instructions when logic might be confusing"
      ]
    },
    {
      title: "Creating Branching Paths",
      description: "Build different survey paths for different respondent types",
      duration: 5,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Branching logic creates entirely different paths through your survey based on respondent 
            characteristics. This is perfect for surveys targeting multiple audiences or complex decision trees.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Branching Examples:
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Card variant="outlined" sx={{ mb: 2, backgroundColor: 'success.lighter' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                  Customer Type Branching
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Branch based on customer type (new, existing, premium):
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="New Customer → Onboarding Questions" size="small" color="primary" />
                  <Chip label="Existing → Satisfaction Questions" size="small" color="secondary" />
                  <Chip label="Premium → Feature Requests" size="small" color="success" />
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2, backgroundColor: 'info.lighter' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                  Product Interest Branching
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Different paths based on product interest:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="Software → Technical Questions" size="small" color="primary" />
                  <Chip label="Services → Service Questions" size="small" color="secondary" />
                  <Chip label="Both → Comprehensive Survey" size="small" color="warning" />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro Tip:</strong> Use branching to create personalized survey experiences. 
              Respondents are more likely to complete surveys that feel relevant to them.
            </Typography>
          </Alert>
        </Box>
      ),
      screenshot: {
        src: "/images/screenshots/tutorial-branching-logic.png",
        alt: "Branching Logic Diagram",
        caption: "Visual flow chart showing different survey branches"
      },
      actions: [
        "Create a 'branching question' early in your survey",
        "Add distinct answer options for each branch",
        "Set up logic rules for each answer option",
        "Create separate question groups for each branch",
        "Link each branch to its appropriate question group",
        "Test all possible paths thoroughly"
      ],
      tips: [
        "Keep branches balanced in length and complexity",
        "Use clear labels for different respondent types",
        "Consider having a 'merge point' where branches rejoin",
        "Document your branching structure for team members"
      ]
    },
    {
      title: "Advanced Logic Combinations",
      description: "Combine multiple logic types for sophisticated surveys",
      duration: 4,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Advanced surveys often combine multiple types of logic to create sophisticated, 
            adaptive experiences. Learn how to use multiple conditions and complex rules.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Advanced Logic Features:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><LogicIcon color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Multiple Conditions" 
                secondary="Use AND/OR logic to combine multiple answer conditions"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LogicIcon color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Cross-Question Logic" 
                secondary="Base logic on answers from multiple previous questions"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LogicIcon color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Nested Logic" 
                secondary="Apply logic within already conditional sections"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LogicIcon color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Dynamic Text" 
                secondary="Change question text based on previous answers"
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Complexity Warning:</strong> Advanced logic can make surveys difficult to manage. 
              Always document your logic rules and test extensively before publishing.
            </Typography>
          </Alert>
        </Box>
      ),
      screenshot: {
        src: "/images/screenshots/tutorial-advanced-logic.png",
        alt: "Advanced Logic Configuration",
        caption: "Complex logic rules with multiple conditions and cross-question dependencies"
      },
      actions: [
        "Identify questions that need complex logic",
        "Map out all possible condition combinations",
        "Set up primary logic conditions first",
        "Add secondary conditions using AND/OR operators",
        "Test each combination thoroughly",
        "Document the logic for future reference"
      ],
      tips: [
        "Start simple and add complexity gradually",
        "Use descriptive names for logic rules",
        "Test edge cases and unusual response patterns",
        "Consider the respondent experience - avoid confusing jumps"
      ]
    },
    {
      title: "Testing and Troubleshooting",
      description: "Ensure your survey logic works perfectly",
      duration: 3,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Testing is crucial for surveys with logic. You need to verify that all paths work correctly 
            and that respondents have a smooth experience regardless of their answers.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Testing Checklist:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Test All Paths" 
                secondary="Go through every possible combination of answers"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Check Edge Cases" 
                secondary="Test unusual or unexpected response patterns"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Verify Question Flow" 
                secondary="Ensure logical progression and no dead ends"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Test on Mobile" 
                secondary="Verify logic works correctly on mobile devices"
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Testing Tip:</strong> Use SurveySlack's preview mode to test different response 
              scenarios. You can reset and try different paths without affecting your survey data.
            </Typography>
          </Alert>
        </Box>
      ),
      screenshot: {
        src: "/images/screenshots/tutorial-logic-testing.png",
        alt: "Logic Testing Interface",
        caption: "Testing survey logic with different response scenarios"
      },
      actions: [
        "Use the preview mode to test your survey",
        "Create a testing checklist for all logic paths",
        "Test with different devices and browsers",
        "Ask colleagues to test unusual response patterns",
        "Document any issues and fix them before publishing",
        "Do a final end-to-end test before going live"
      ],
      tips: [
        "Keep a spreadsheet of all logic rules for reference",
        "Test immediately after adding each logic rule",
        "Use realistic test data that matches your target audience",
        "Pay attention to survey completion times with logic"
      ]
    }
  ];

  const handleTutorialComplete = () => {
    console.log('Survey Logic Tutorial completed!');
  };

  return (
    <Box>
      <Tutorial
        title="Setting Up Survey Logic & Branching"
        description="Master advanced survey features like skip logic, branching, and conditional questions to create dynamic, personalized survey experiences that adapt to each respondent."
        estimatedTime={20}
        difficulty="Intermediate"
        steps={tutorialSteps}
        onComplete={handleTutorialComplete}
      />

      {/* Next Steps */}
      <Card sx={{ mt: 4, background: 'linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
            🚀 Advanced Survey Features
          </Typography>
          <Typography variant="body1" paragraph>
            Now that you've mastered survey logic, explore these advanced capabilities:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" href="/app/documentations/creating-surveys/question-types">
              Advanced Question Types
            </Button>
            <Button variant="outlined" href="/app/documentations/publishing/share-options">
              Distribution Strategies
            </Button>
            <Button variant="outlined" href="/app/documentations/analytics/response-analytics">
              Advanced Analytics
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SurveyLogicTutorial;